import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, MenuItem, Paper, Stack, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import {
  collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp, orderBy,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { DEFAULT_ALLOWANCE_TYPES, type AllowanceType, type AllowanceCalcType } from '../utils/allowance';
import { getAllowanceTypes, getNpStaff, getStoredStore } from '../services/nightProApi';

import { pctToRate } from '../utils/numberGuard';
/**
 * 手当マスタ（同伴・指名・ボトルバック等）。
 *
 * これまで手当は NightPro(Laravel) 側にしか無く、Firestore の給与計算に入っていなかった。
 * ここに持たせることで、給与を1本にできる。
 */

const yen = (n: number) => `¥${Math.round(n).toLocaleString()}`;

const emptyForm = {
  name: '', calcType: 'fixed' as AllowanceCalcType, amount: 0, backRate: 50,
  showOnReceipt: true, includeInSales: true, isActive: true,
};

const AllowanceTypes: React.FC = () => {
  const { shop } = useAuth();
  const [rows, setRows] = useState<AllowanceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [dialog, setDialog] = useState<{ open: boolean; id: string | null }>({ open: false, id: null });
  const [form, setForm] = useState(emptyForm);
  const [importing, setImporting] = useState(false);
  const [importPlan, setImportPlan] = useState<any | null>(null);

  const load = useCallback(async () => {
    if (!shop) return;
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(query(
        collection(db, 'allowanceTypes'),
        where('shopId', '==', shop.id),
        orderBy('sortOrder', 'asc'),
      ));
      setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
    } catch (e: any) {
      setError(e?.message ?? '手当マスタの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [shop]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyForm); setDialog({ open: true, id: null }); };
  const openEdit = (r: AllowanceType) => {
    setForm({
      name: r.name, calcType: r.calcType,
      amount: r.calcType === 'percent' ? Math.round(r.amount * 1000) / 10 : r.amount,
      backRate: Math.round(r.backRate * 100),
      showOnReceipt: r.showOnReceipt, includeInSales: r.includeInSales, isActive: r.isActive,
    });
    setDialog({ open: true, id: r.id });
  };

  const save = async () => {
    if (!shop || !form.name.trim()) return;
    const payload = {
      shopId: shop.id,
      name: form.name.trim(),
      calcType: form.calcType,
      amount: form.calcType === 'percent' ? form.amount / 100 : form.amount,
      backRate: pctToRate(form.backRate),
      showOnReceipt: form.showOnReceipt,
      includeInSales: form.includeInSales,
      isActive: form.isActive,
      sortOrder: dialog.id ? (rows.find((r) => r.id === dialog.id)?.sortOrder ?? rows.length) : rows.length,
      updatedAt: Timestamp.now(),
    };
    try {
      if (dialog.id) await updateDoc(doc(db, 'allowanceTypes', dialog.id), payload);
      else await addDoc(collection(db, 'allowanceTypes'), { ...payload, createdAt: Timestamp.now() });
      setDialog({ open: false, id: null });
      await load();
    } catch (e: any) {
      setError(e?.message ?? '保存に失敗しました');
    }
  };

  const remove = async (r: AllowanceType) => {
    try { await deleteDoc(doc(db, 'allowanceTypes', r.id)); await load(); }
    catch (e: any) { setError(e?.message ?? '削除に失敗しました'); }
  };

  const seedDefaults = async () => {
    if (!shop) return;
    try {
      for (const t of DEFAULT_ALLOWANCE_TYPES) {
        await addDoc(collection(db, 'allowanceTypes'), {
          ...t, shopId: shop.id, createdAt: Timestamp.now(), updatedAt: Timestamp.now(),
        });
      }
      setInfo('よく使う手当を入れました。金額とバック率を店に合わせて直してください。');
      await load();
    } catch (e: any) {
      setError(e?.message ?? '初期値の投入に失敗しました');
    }
  };

  /** NightPro の台帳を取り込む。NightPro へは画面から呼ぶ（トークンを持っているため）。 */
  const importFromNightPro = async (dryRun: boolean) => {
    if (!shop) return;
    if (!getStoredStore()) {
      setError('先に「夜職Proモード」で店舗アカウントにログインしてください。');
      return;
    }
    setImporting(true);
    setError(null);
    try {
      const [npAllowances, npStaff] = await Promise.all([getAllowanceTypes(), getNpStaff()]);
      const fn = httpsCallable(getFunctions(undefined, 'asia-northeast1'), 'importFromNightPro');
      const res: any = await fn({
        shopId: shop.id, dryRun,
        allowanceTypes: npAllowances,
        staff: npStaff,
      });
      if (dryRun) {
        setImportPlan(res.data);
      } else {
        setImportPlan(null);
        setInfo(
          `取り込みました。手当 ${res.data.allowances.length} 件 / スタッフ ${res.data.staff.length} 名。` +
          (res.data.unmatched.length ? ` ${res.data.unmatched.length} 件は突き合わせできませんでした。` : '')
        );
        await load();
      }
    } catch (e: any) {
      setError(e?.message ?? '取り込みに失敗しました');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" fontWeight="bold">手当マスタ</Typography>
          <Typography variant="body2" color="text.secondary">
            同伴・指名・ボトルバックなど、商品ではない売上とキャストへのバック
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<CloudDownloadIcon />} disabled={importing}
            onClick={() => importFromNightPro(true)}>
            夜職Proから取り込む
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>手当を追加</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>手当がまだありません</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            指名・同伴・ボトルバックを登録すると、会計で卓に付けられるようになり、
            給与にも自動で乗ります。
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={seedDefaults}>よく使う手当を入れる</Button>
            <Button onClick={() => importFromNightPro(true)} disabled={importing}>
              夜職Proから取り込む
            </Button>
          </Stack>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>手当名</TableCell>
                <TableCell>計算</TableCell>
                <TableCell align="right">金額</TableCell>
                <TableCell align="right">バック率</TableCell>
                <TableCell>売上計上</TableCell>
                <TableCell>レシート</TableCell>
                <TableCell>状態</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover sx={{ opacity: r.isActive ? 1 : 0.5 }}>
                  <TableCell>
                    {r.name}
                    {r.nightProId != null && <Chip size="small" label="夜職Pro由来" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>{r.calcType === 'percent' ? '小計に対する割合' : '定額'}</TableCell>
                  <TableCell align="right">
                    {r.calcType === 'percent' ? `${Math.round(r.amount * 1000) / 10}%` : yen(r.amount)}
                  </TableCell>
                  <TableCell align="right">{Math.round(r.backRate * 100)}%</TableCell>
                  <TableCell>{r.includeInSales ? 'する' : 'しない'}</TableCell>
                  <TableCell>{r.showOnReceipt ? '出す' : '出さない'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.isActive ? '有効' : '無効'} color={r.isActive ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(r)}>編集</Button>
                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => remove(r)}>削除</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, id: null })} fullWidth maxWidth="sm">
        <DialogTitle>{dialog.id ? '手当を編集' : '手当を追加'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="手当名" fullWidth value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="例: 本指名 / 同伴 / ボトルバック" />
            <TextField select label="計算方法" fullWidth value={form.calcType}
              onChange={(e) => setForm({ ...form, calcType: e.target.value as AllowanceCalcType })}>
              <MenuItem value="fixed">定額（1件あたり◯円）</MenuItem>
              <MenuItem value="percent">割合（会計小計の◯%）</MenuItem>
            </TextField>
            <TextField type="number" fullWidth
              label={form.calcType === 'percent' ? '小計に対する割合（%）' : '金額（円）'}
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: Math.max(0, Number(e.target.value)) })} />
            <TextField type="number" fullWidth label="キャストへのバック率（%）"
              value={form.backRate}
              onChange={(e) => setForm({ ...form, backRate: Math.min(100, Math.max(0, Number(e.target.value))) })}
              helperText="店が取る額のうち、キャストへ渡る割合" />
            <FormControlLabel
              control={<Switch checked={form.includeInSales}
                onChange={(e) => setForm({ ...form, includeInSales: e.target.checked })} />}
              label="売上として計上する（分配やインセンティブの母数に入れる）" />
            <FormControlLabel
              control={<Switch checked={form.showOnReceipt}
                onChange={(e) => setForm({ ...form, showOnReceipt: e.target.checked })} />}
              label="レシートに項目として印字する" />
            <FormControlLabel
              control={<Switch checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />}
              label="有効" />
            {!form.includeInSales && (
              <Alert severity="info">
                売上に計上しない手当は、客への請求には乗りますが、
                分配やインセンティブの母数からは外れます。ボトルバックのような報奨向けです。
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, id: null })}>やめる</Button>
          <Button variant="contained" onClick={save} disabled={!form.name.trim()}>保存</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!importPlan} onClose={() => setImportPlan(null)} fullWidth maxWidth="md">
        <DialogTitle>夜職Proの台帳を取り込む</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>やること</AlertTitle>
            夜職Pro側にしか無かった<strong>手当マスタ</strong>と<strong>スタッフの給与条件</strong>（時給・日給・歩合率・バック対象か）を
            こちらへ持ってきます。名前で突き合わせ、同じ人には上書きせず給与条件だけを足します。
            取り込み後は、この画面と「給与」だけで支給額が出せます。
          </Alert>
          {importPlan && (
            <>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                手当マスタ {importPlan.allowances.length} 件
              </Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead><TableRow><TableCell>手当名</TableCell><TableCell>内容</TableCell><TableCell>扱い</TableCell></TableRow></TableHead>
                <TableBody>
                  {importPlan.allowances.map((a: any) => (
                    <TableRow key={a.nightProId}>
                      <TableCell>{a.name}</TableCell>
                      <TableCell>{a.detail}</TableCell>
                      <TableCell><Chip size="small" label={a.action === 'create' ? '新規' : '更新'} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                スタッフ {importPlan.staff.length} 名
              </Typography>
              <Table size="small">
                <TableHead><TableRow><TableCell>夜職Proの名前</TableCell><TableCell>統合先</TableCell><TableCell>持ち込む条件</TableCell><TableCell>扱い</TableCell></TableRow></TableHead>
                <TableBody>
                  {importPlan.staff.map((s: any) => (
                    <TableRow key={s.nightProId}>
                      <TableCell>{s.nightProName}</TableCell>
                      <TableCell>{s.employeeName}</TableCell>
                      <TableCell>{s.changes.join(' / ') || '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" color={s.action === 'update' ? 'success' : 'default'}
                          label={s.action === 'update' ? '既存の従業員へ' : '新しく作る'} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {importPlan.unmatched.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <AlertTitle>取り込めない {importPlan.unmatched.length} 件</AlertTitle>
                  {importPlan.unmatched.map((u: any) => (
                    <div key={u.nightProId}>{u.name}: {u.reason}</div>
                  ))}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportPlan(null)} disabled={importing}>やめる</Button>
          <Button variant="contained" disabled={importing} onClick={() => importFromNightPro(false)}>
            この内容で取り込む
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllowanceTypes;
